import { createEnv } from "@t3-oss/env-core";
import { z } from "zod";

const runtimeEnv = import.meta.env ?? process.env;

const booleanSchema = z
  .string()
  .refine((s) => s === "true" || s === "false")
  .transform((s) => s === "true")
  .default("false");

const isProd = runtimeEnv.REACT_APP_ENV === "production";
const isTest = runtimeEnv.NODE_ENV === "test";

const validatedEnv = createEnv({
  server: {
    ANALYZE: booleanSchema,
    CI: booleanSchema,
    SENTRY_AUTH_TOKEN: z.string().optional(),
    SENTRY_RELEASE: z.string().optional(),

    HEROKU_BUILD_COMMIT: z.string().optional(),
    HEROKU_APP_NAME: z.string().optional(),

    BASE_URL: z.string().url().optional().catch(undefined),
  },

  clientPrefix: "REACT_APP_",
  client: {
    REACT_APP_ENV: z.enum(["production", "staging", "development", "automation", "local", "test"]).default("local"),
    REACT_APP_SG_API_URL: z
      .string()
      .url()
      .transform((url) => (url.endsWith("/") ? url : `${url}/`)),
    REACT_APP_STRIPE_KEY: z.string(),
    REACT_APP_STRIPE_KEY_QWICK: z.string(),
    REACT_APP_CLOUDINARY_CLOUD_NAME: z.string(),
    REACT_APP_DEFAULT_LOCATION_IMAGE_ID: z.string(),

    REACT_APP_GOOGLE_API_KEY: z.string(),
    REACT_APP_SENDBIRD_APP_ID: z.string(),
    REACT_APP_LAUNCH_DARKLY_ID: z.string(),
    REACT_APP_E2E_LAUNCH_DARKLY_FLAG_SOURCE: z.string().optional(),

    REACT_APP_BUSINESS_SUPPORT_URL: z.string().url(),
    REACT_APP_CONTACT_SUPPORT_URL: z.string().url(),
    REACT_APP_TOS_URL: z.string().url(),
    REACT_APP_PRIVACY_URL: z.string().url(),

    REACT_APP_SENTRY_DSN: z.string().optional(),

    REACT_APP_SEGMENT_WRITE_KEY: z.string().optional(),
    REACT_APP_SEGMENT_CDN_URL: z.string().url().optional(),

    REACT_APP_BRAZE_KEY: z.string().optional(),
    REACT_APP_BRAZE_ENDPOINT: z.string().optional(),

    REACT_APP_TESTING_API_ACCESS_KEY: z.string().optional(),

    REACT_APP_GTM_ID: z.string().optional().default(""),
    REACT_APP_GTM_AUTH: z.string().optional().default(""),
    REACT_APP_GTM_ENV: z.string().optional().default(""),
  },
  createFinalSchema: (shape) => {
    const schema = z.object(shape);

    if (isTest) {
      // If we are in test mode, we want to allow most env variables to be optional but without breaking the type
      return schema.partial().required({
        REACT_APP_ENV: true,
        REACT_APP_CLOUDINARY_CLOUD_NAME: true,
        REACT_APP_DEFAULT_LOCATION_IMAGE_ID: true,
      }) as unknown as typeof schema;
    }
    if (!isProd) {
      // if REACT_APP_E2E_LAUNCH_DARKLY_FLAG_SOURCE is set we don't need REACT_APP_LAUNCH_DARKLY_ID
      if (runtimeEnv.REACT_APP_E2E_LAUNCH_DARKLY_FLAG_SOURCE) {
        return schema.omit({ REACT_APP_LAUNCH_DARKLY_ID: true }).extend({
          REACT_APP_LAUNCH_DARKLY_ID: shape.REACT_APP_LAUNCH_DARKLY_ID.optional().default(""),
        });
      }
      return schema;
    }
    return schema.required({
      REACT_APP_SEGMENT_WRITE_KEY: true,
      REACT_APP_BRAZE_KEY: true,
      REACT_APP_BRAZE_ENDPOINT: true,
      REACT_APP_SENTRY_DSN: true,
      SENTRY_AUTH_TOKEN: true,
    });
  },
  runtimeEnv,
  emptyStringAsUndefined: true,
});

export const env = {
  ...validatedEnv,
  IS_LOCAL: runtimeEnv.DEV,
  IS_DEV: !isProd && validatedEnv.REACT_APP_ENV !== "staging",
  IS_PROD: isProd,
  IS_AUTOMATION: isTest || validatedEnv.REACT_APP_ENV === "automation" || validatedEnv.REACT_APP_ENV === "test",
};
